package com.tovtalk.ui.screen.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.ChatThread
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.chat.ChatListViewModel
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

@Composable
fun ChatRequestsScreen(
    onNavigateBack: () -> Unit,
    onNavigateToChat: (Int, String) -> Unit,
    onNavigateToStories: ((Int) -> Unit)? = null,
    viewModel: ChatListViewModel = koinInject()
) {
    val requestsState by viewModel.chatRequests.collectAsState()

    DisposableEffect(Unit) {
        onDispose { viewModel.onClear() }
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("chat_requests_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        Box(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
            when (val state = requestsState) {
                is UiState.Loading -> {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = TovTalkTheme.colors.brand
                    )
                }
                is UiState.Error -> {
                    Text(
                        text = state.message,
                        modifier = Modifier.align(Alignment.Center),
                        color = TovTalkTheme.colors.error
                    )
                }
                is UiState.Success -> {
                    val list = (state as UiState.Success<List<ChatThread>>).data
                    if (list.isEmpty()) {
                        Text(
                            text = StringProvider.get("chat_no_requests"),
                            modifier = Modifier.align(Alignment.Center),
                            style = TovTalkTheme.typography.bodyLarge
                        )
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(TovTalkTheme.spacing.screenPadding),
                            verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.sm)
                        ) {
                            items(list, key = { it.otherUser.id }) { thread ->
                                RequestItem(
                                    thread = thread,
                                    onAccept = { viewModel.acceptRequest(thread) },
                                    onDecline = { viewModel.declineRequest(thread) },
                                    onClick = {
                                        onNavigateToChat(thread.otherUser.id, thread.otherUser.fullname)
                                    },
                                    onStoryClick = if (thread.otherUser.hasActiveStory && onNavigateToStories != null) { { onNavigateToStories(thread.otherUser.id) } } else null
                                )
                            }
                        }
                    }
                }
                else -> {}
            }
        }
    }
}

@Composable
private fun RequestItem(
    thread: ChatThread,
    onAccept: () -> Unit,
    onDecline: () -> Unit,
    onClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = TovTalkTheme.spacing.xs),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = thread.otherUser.profilePhoto,
            size = TovTalkTheme.sizes.avatarLg,
            hasStory = thread.otherUser.hasActiveStory && onStoryClick != null,
            onStoryClick = onStoryClick
        )
        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.screenPadding))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = thread.otherUser.fullname,
                style = TovTalkTheme.typography.titleMedium,
                color = TovTalkTheme.colors.textPrimary
            )
            thread.lastMessage?.let { lastMsg ->
                Text(
                    text = lastMsg.content ?: "",
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textSecondary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        Row {
            IconButton(onClick = onDecline) {
                Icon(Icons.Default.Close, contentDescription = StringProvider.get("common_reject"), tint = TovTalkTheme.colors.error)
            }
            IconButton(onClick = onAccept) {
                Icon(Icons.Default.Check, contentDescription = StringProvider.get("common_accept"), tint = TovTalkTheme.colors.brand)
            }
        }
    }
}
